<template>
  <div>
    <!-- Filters -->
    <filters
      v-show="!errorContent.filters"
      :periodo-filter.sync="configRequest.periodo"
      :modalidad-filter.sync="configRequest.modalidad"
      :periodo-options="periodoOptions"
      :modalidad-options="modalidadOptions"
      :loader="loader.filters"
    />

    <!-- Table Container Card -->
    <b-overlay
      :show="isBusyTable"
      rounded="sm"
    >
      <b-card
        v-show="!errorContent.table"
        no-body
        class="mb-0"
      >
        <!-- Datos cargados -->
        <b-card-header class="pb-0">
          <b-card-title />
          <b-card-text class="font-small-2 mr-25 mb-0">
            Datos cargados <b>{{ updatedAt }}</b>
          </b-card-text>
        </b-card-header>

        <!-- Table Top -->
        <div class="m-2">
          <b-row>
            <!-- Cantidad de elementos por página -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <span>Mostrar</span>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
              <!-- Nuevo registro -->
              <b-button
                variant="primary"
                to="/servicio-escolar/nuevo-curso"
              >
                Nuevo registro
              </b-button>
            </b-col>

            <!-- Buscador -->
            <b-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-end">
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon
                      :class="{ 'text-danger cursor-pointer': searchQuery != '' }"
                      :icon="searchQuery != '' ? 'XIcon' : 'SearchIcon'"
                      @click="searchQuery != '' ? searchQuery = '' : ''"
                    />
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="searchQuery"
                    placeholder="Buscar"
                  />
                </b-input-group>
              </div>
            </b-col>
          </b-row>
        </div>

        <b-table
          ref="refDataListTable"
          class="position-relative"
          empty-text="No se encontraron resultados"
          primary-key="id"
          responsive
          show-empty
          striped
          :items="fetchData"
          :fields="tableColumns"
          :sort-desc.sync="isSortDirDesc"
          :sort-by.sync="sortBy"
        >
          <!-- Column: ID y edición -->
          <template #cell(id)="data">
            <div class="text-nowrap text-center">
              <b-link
                :to="`/servicio-escolar/edicion-curso/${data.item.id}`"
                class="font-weight-bold text-secondary"
              >
                <feather-icon
                  :id="`curso-row-${data.item.id}-edit-icon`"
                  icon="SettingsIcon"
                  class="cursor-pointer"
                  size="16"
                  :class="data.item.activo ? 'mx-1' : 'mr-1'"
                />
              </b-link>
              <b-tooltip
                title="Editar registro"
                placement="right"
                :target="`curso-row-${data.item.id}-edit-icon`"
              />
              <span>{{ data.item.id }}</span>
            </div>
          </template>
          <!-- Column: Materia -->
          <template #cell(materia)="data">
            <b-media vertical-align="center">
              <small class="font-weight-bold d-block text-nowrap text-primary">
                <b>Grupo {{ data.item.grupo }}</b>
              </small>
              <small class="text-dark font-weight-bold">{{ data.item.materia }}</small>
            </b-media>
          </template>
          <!-- Column: Modalidad -->
          <template #cell(modalidad)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                variant="light-primary"
                rounded
              >
                <feather-icon
                  icon="TrelloIcon"
                  size="18"
                />
              </b-avatar>
              <div class="ml-50">
                <h5 class="mb-0">
                  <b>{{ data.item.modalidad }}</b>
                </h5>
                <small>{{ nombreModalidad(data.item.modalidad) }}</small>
              </div>
            </div>
          </template>
          <!-- Column: Parciales -->
          <template #cell(num_parciales)="data">
            <div class="text-center">
              <b-badge
                :id="`popover-parciales-curso-${data.item.id}`"
                :variant="parciales(data.item.num_parciales, data.item.parciales_creados).variant"
              >
                {{ parciales(data.item.num_parciales, data.item.parciales_creados).label }}
              </b-badge>

              <b-popover
                :target="`popover-parciales-curso-${data.item.id}`"
                placement="right"
                triggers="hover"
                :variant="parciales(data.item.num_parciales, data.item.parciales_creados).variant"
              >
                <template #title>
                  <div class="text-center">
                    <small><b>{{ parciales(data.item.num_parciales, data.item.parciales_creados).label }}</b></small>
                  </div>
                </template>
                <small>{{ parciales(data.item.num_parciales, data.item.parciales_creados).description }}</small>
              </b-popover>

            </div>
          </template>
          <!-- Periodo -->
          <template #cell(periodo)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                variant="light-primary"
                rounded
              >
                <feather-icon
                  icon="CalendarIcon"
                  size="18"
                />
              </b-avatar>
              <div class="ml-50">
                <h5 class="mb-0">
                  Periodo: <b>{{ data.item.clave }}</b>
                </h5>
                <small>
                  <b-badge
                    variant="light-success"
                    style="display: inline;"
                    class="mr-25"
                  >
                    Inicia: {{ timestampToDate(data.item.fecha_ini) }}
                  </b-badge>
                  <b-badge
                    variant="light-secondary"
                    style="display: inline;"
                  >
                    Termina: {{ timestampToDate(data.item.fecha_ini) }}
                  </b-badge>
                </small>
              </div>
            </div>
          </template>
        </b-table>

        <!-- Table's footer & pagination -->
        <div class="mx-2 mb-2">
          <b-row>
            <!-- Leyenda de cuantos elementos se muestran -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">Mostrando {{ dataMeta.from }} a {{ dataMeta.to }} de {{ dataMeta.of }} registros</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >

              <b-pagination
                v-model="currentPage"
                :total-rows="totalItems"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>
          </b-row>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BAvatar,
  BMedia,
  BBadge,
  BPagination,
  BCardHeader,
  BCardText,
  BCardTitle,
  BTooltip,
  BButton,
  BInputGroup,
  BInputGroupPrepend,
  BLink,
  BOverlay,
  BPopover,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  ref, onMounted, watch,
} from '@vue/composition-api'
import { lastUpdate, timestampToDate, nombreModalidad } from '@core/utils/filter'
import { useServicioEscolarCatalogos, configTableCursos } from '@/views/campus/servicio-escolar/useServicioEscolar'
import { useFillDataTable } from '@core/components/data-table/useFillDataTable'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Filters from './components/Filters.vue'

export default {
  components: {
    Filters,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BAvatar,
    BMedia,
    BCardHeader,
    BCardTitle,
    BCardText,
    BInputGroup,
    BInputGroupPrepend,
    BTooltip,
    BButton,
    BLink,
    BBadge,
    BPagination,
    vSelect,
    BOverlay,
    BPopover,
  },
  setup() {
    const toast = useToast()
    const updatedAt = ref(lastUpdate())
    const loader = ref({ filters: false })
    const errorContent = ref({ filters: false, table: false })
    const { tableColumns, configRequest } = configTableCursos()
    const { modalidadOptions, periodoOptions, periodoOptionsPromise } = useServicioEscolarCatalogos()
    const {
      //* Fetching data
      fetchData, refetchData, refDataListTable,
      //* Pagination
      dataMeta,
      //* Table dynamic params
      dynamicParamRequest,
      //* Basic filters & pagination
      perPage, totalItems, currentPage, perPageOptions, searchQuery, sortBy, isSortDirDesc,
      //* Loader
      isBusyTable,
      //* Error
      errorRequest,
    } = useFillDataTable()

    //* Method to load filters, this makes promises, and wait to finish both of them to remove overlay
    const fetchCatalogosFilters = () => {
      loader.value.filters = true
      periodoOptionsPromise()
        .then(response => {
          periodoOptions.value = response
          loader.value.filters = false
        })
        .catch(error => {
          loader.value.filters = false
          errorContent.value.filters = true
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error en petición [Filtro de periodos]',
              text: `${error} Descuida, puedes seguir trabajando con la tabla de resultados, nosotros vemos la manera de solucionar el inconveniente ;)`,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          },
          { timeout: 10000 })
        })
    }

    //* Check how many exams has the course
    const parciales = (requeridos, creados) => {
      if (requeridos === 0 && creados === 0) return { label: 'Sin parciales', variant: 'danger', description: 'No ha asignado/creado ningún parcial a este curso.' }
      if (requeridos > 0 && creados !== requeridos) return { label: 'Parciales asignados pero sin crear', variant: 'warning', description: 'Este curso tiene parciales asignados, pero no han sido creados.' }
      return { label: `${creados} parcial${creados > 1 ? 'es' : ''} disponible${creados > 1 ? 'es' : ''}`, variant: 'primary', description: 'Este curso cuenta con parciales correctamente creados y asignados.' }
    }

    //* On mounted
    onMounted(() => {
      fetchCatalogosFilters() //* Load filter options (selects)
      dynamicParamRequest.value = configRequest.value //* Asign config params for each module as dynamic params
    })

    watch(errorRequest, error => { if (error) errorContent.value.table = error }) //* If the flag 'errorRequest' from data request is true, hide table content
    watch(configRequest.value, () => { refetchData() }) //* Any change in config params (filters), refetch table data

    return {
      fetchData,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refDataListTable,
      refetchData,
      parciales,

      // Filter
      lastUpdate,
      updatedAt,
      timestampToDate,
      nombreModalidad,

      // Filter's option
      periodoOptions,
      modalidadOptions,

      // Extra Filters
      configRequest,

      // Loader
      loader,
      isBusyTable,

      // Error
      errorContent,
    }
  },
}
</script>
